import React, { useMemo, useState } from 'react';
import SortableTable from '../SortableTable';
import { useDN } from '../../contexts/DNContext';
import { _t, _n } from '../../utils/i18n';

const DNProductsTable = ({ products, onProductClick, obfuscate }) => {
  const [filter, setFilter] = useState('');
  const { presetValue } = useDN();

  // Préparation des données avec la colonne DN calculée et évolution
  const productsWithDN = useMemo(() => {
    if (!products || products.length === 0) return [];

    return products.map(product => {
      const totalRelevantShops = product.presentShops + product.notPresentShops;
      const dnPercentage = totalRelevantShops > 0
        ? (product.presentShops / totalRelevantShops) * 100
        : 0;

      // Calcul de la DN pour la période précédente
      const totalPrevRelevantShops = product.prev_presentShops + product.prev_notPresentShops;
      const prevDnPercentage = totalPrevRelevantShops > 0
        ? (product.prev_presentShops / totalPrevRelevantShops) * 100
        : 0;

      // Calcul de l'évolution
      let evolution = 0;
      let evolutionText = '0';

      if (prevDnPercentage > 0) {
        evolution = dnPercentage - prevDnPercentage;
        evolutionText = (evolution > 0 ? '+' : '') + evolution.toFixed(1);
      } else if (dnPercentage > 0) {
        evolution = dnPercentage;
        evolutionText = '+' + dnPercentage.toFixed(1);
      }

      return {
        ...product,
        dn: dnPercentage.toFixed(1),
        _dn: dnPercentage, // valeur brute pour le tri
        prevDn: prevDnPercentage.toFixed(1),
        evolution: evolution,
        evolutionText: evolutionText,
        totalShops: product.presentShops + product.notPresentShops + product.noRefShops
      };
    });
  }, [products]);

  // Définition des colonnes
  const columns = useMemo(() => [
    {
      key: 'value',
      label: _t('Product'),
      sortFunction: (a, b, direction) => {
        return direction === 'ascending'
          ? a.value.localeCompare(b.value)
          : b.value.localeCompare(a.value);
      },
      render: (item) => (
        <div className="font-medium flex items-center gap-2">
          <button onClick={() => onProductClick(item.value)}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 0l-2 2a1 1 0 101.414 1.414L8 10.414l1.293 1.293a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </button>
          {obfuscate ? `${item.value.slice(0, 2)}${'*'.repeat(Math.max(0, item.value.length - 2))}` : item.value}
        </div>
      )
    },
    {
      key: 'dn',
      label: _t('ND'),
      sortFunction: (a, b, direction) => {
        return direction === 'ascending'
          ? a._dn - b._dn
          : b._dn - a._dn;
      },
      render: (item) => (
        <div className="font-medium">{item.dn}%</div>
      )
    },
    {
      key: 'prevDn',
      label: _t('Previous ND'),
      sortFunction: (a, b, direction) => {
        return direction === 'ascending'
          ? a.prevDn - b.prevDn
          : b.prevDn - a.prevDn;
      },
      render: (item) => (
        <div className="font-medium">{item.prevDn}%</div>
      )
    },
    {
      key: 'evolutionText',
      label: _t('Evolution'),
      sortFunction: (a, b, direction) => {
        return direction === 'ascending'
          ? a.evolution - b.evolution
          : b.evolution - a.evolution;
      },
      render: (item) => {
        // Déterminer la couleur de l'évolution
        let colorClass = 'text-gray-500';
        if (item.evolution > 0) colorClass = 'text-green-600 dark:text-green-400';
        if (item.evolution < 0) colorClass = 'text-red-600 dark:text-red-400';

        return (
          <div className={`flex items-center font-medium ${colorClass}`}>
            {item.evolution > 0 && (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            )}
            {item.evolution < 0 && (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )}
            {item.evolutionText}
          </div>
        );
      }
    },
    {
      key: 'presentShops',
      label: _t('Presences'),
      sortFunction: (a, b, direction) => {
        return direction === 'ascending'
          ? a.presentShops - b.presentShops
          : b.presentShops - a.presentShops;
      }
    },
    {
      key: 'notPresentShops',
      label: _t('Absences'),
      sortFunction: (a, b, direction) => {
        return direction === 'ascending'
          ? a.notPresentShops - b.notPresentShops
          : b.notPresentShops - a.notPresentShops;
      }
    },
    {
      key: 'noRefShops',
      label: _t('No Ref.'),
      sortFunction: (a, b, direction) => {
        return direction === 'ascending'
          ? a.noRefShops - b.noRefShops
          : b.noRefShops - a.noRefShops;
      }
    },
    {
      key: 'totalShops',
      label: _t('Total Shops'),
      sortFunction: (a, b, direction) => {
        return direction === 'ascending'
          ? a.totalShops - b.totalShops
          : b.totalShops - a.totalShops;
      }
    },
    // {
    //   key: 'category',
    //   label: _t('Category'),
    //   rawValue: (item) => item.category ? item.category : 'Sans catégorie',
    //   render: (item) => item.category ? item.category : 'Sans catégorie',
    //   sortFunction: (a, b, direction) => {
    //     return direction === 'ascending'
    //       ? (a.category || '').localeCompare(b.category || '')
    //       : (b.category || '').localeCompare(a.category || '');
    //   }
    // },
  ], []);

  const filteredProducts = useMemo(() => {
    return productsWithDN.filter(product => product.value.toLowerCase().includes(filter.toLowerCase()));
  }, [productsWithDN, filter]);

  return (
    <div className="space-y-4 sm:p-4 p-2">

      <div className="relative">
        <input
          type="text"
          placeholder={_t('Search a product')}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        />
        {filter && (
          <button
            onClick={() => setFilter('')}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            aria-label="Clear search"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </button>
        )}
      </div>

      <SortableTable
        data={filteredProducts}
        columns={columns}
        emptyMessage={_t('No product found for this project')}
        countLabel={`Total: ${productsWithDN.length} ${_n('product', 'products', productsWithDN.length)}`}
        initialSortBy={{ key: 'dn', direction: 'descending' }}
        exportable={true}
        exportFilename={`dn_produits_${presetValue}.csv`}
        onRowClick={(item) => onProductClick(item.value)}
        disableFilters
      />
    </div>
  );
};

export default DNProductsTable;